import { useEffect, useState } from "react";
import { ArrowLeft, Clock3 } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { STAGES } from "@/constants/content";

const COPY = {
  2: { lead: "Describe your idea, pick your country and city or pin-code, and name your target audience. Your draft is encrypted server-side and kept for 2 hours, so nothing is lost while you sign in with Google.",
       bullets: ["Idea, country, city/pin-code & audience capture", "Encrypted 2-hour draft that survives sign-in", "1 Free Assessment on Free Explorer"] },
  3: { lead: "Your idea scored on 12 universal + 8 industry-overlay criteria, with cited evidence, 3 geographic lenses and a 7-section 4-tile bento teardown.",
       bullets: ["Deterministic 0–100 score with verdict band", "Fatal-flaw detection + recommended safe pivot", "Copy-paste B2B & B2C discovery scripts"] },
  4: { lead: "A 3D visual concept of your product and a mobile-responsive test website with brand, hero copy and features — plus the Agency vs AI cost anchor.",
       bullets: ["3D concept render", "Test site at /preview/{slug} (subdomain at launch)", "Agency vs AI Cost Anchor Matrix"] },
  5: { lead: "A 10-question founder intake feeding a 16-chapter investor-grade dossier, plus the Elite human + legal incorporation concierge.",
       bullets: ["16-chapter dossier with PDF export", "24-month 3-scenario financial model", "CA/CS/Legal registration concierge (Elite)"] },
};

const useStageData = (stage) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    if (stage === 3) api.get("/criteria", { params: { group: "universal" } }).then((r) => setData(r.data.map((c) => `${c.name} · ${c.weight}`))).catch(() => setData(null));
    if (stage === 5) api.get("/growth/templates").then((r) => setData(r.data.dossier_chapters.map((c) => `${c.number}. ${c.title}`))).catch(() => setData(null));
  }, [stage]);
  return data;
};

const RealList = ({ title, items }) => (
  <div className="iv-glass rounded-2xl p-5" data-testid="coming-next-real-data">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</div>
    <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
      {items.map((t) => <li key={t} className="truncate text-xs text-slate-300">{t}</li>)}
    </ul>
  </div>
);

export const ComingNextPanel = ({ stage, onStage }) => {
  const meta = STAGES[stage - 1];
  const copy = COPY[stage];
  const items = useStageData(stage);
  const { activeReport } = useApp();
  const preview = activeReport?.website_previews?.[0];
  return (
    <section className="grid gap-6 py-10 lg:grid-cols-[1.1fr_1fr]" aria-labelledby="stage-title" data-testid={`coming-next-panel-${stage}`}>
      <div className="iv-rise min-w-0">
        <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/[0.07] px-3 py-1 text-[11px] font-semibold text-amber-300" data-testid="coming-next-badge">
          <Clock3 className="h-3.5 w-3.5" aria-hidden="true" /> Coming next · being built now
        </span>
        <h1 id="stage-title" className="mt-4 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">{meta.code}. {meta.title}</h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">{copy.lead}</p>
        <ul className="mt-5 space-y-2">{copy.bullets.map((b) => <li key={b} className="text-sm text-slate-300">— {b}</li>)}</ul>
        <button type="button" onClick={() => onStage(1)} className="mt-7 inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-slate-200 hover:border-cyan-400/50" data-testid="coming-next-back-button">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to overview & live case studies
        </button>
      </div>
      <div className="iv-rise iv-delay-2 min-w-0 space-y-4">
        {stage === 3 && items && <RealList title="The 12 universal criteria & weights (live from the engine)" items={items} />}
        {stage === 5 && items && <RealList title="The 16 dossier chapters" items={items} />}
        {stage === 4 && preview && (
          <div className="iv-glass rounded-2xl p-5" data-testid="coming-next-preview-spec">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Reference preview spec · {preview.brand_name}</div>
            {mediaUrl(preview.mockup_image_url) && <img src={mediaUrl(preview.mockup_image_url)} alt={`3D concept for ${preview.brand_name}`} className="mt-3 aspect-square w-full max-w-xs rounded-xl object-cover" loading="lazy" />}
            <p className="mt-3 text-sm font-bold text-white">{preview.hero_headline}</p>
            <p className="mt-1 text-xs text-slate-400">{preview.hero_subhead}</p>
          </div>
        )}
        {stage === 2 && <RealList title="What we will ask" items={["Your idea in plain words", "Country", "City / region / pin-code", "Target audience"]} />}
      </div>
    </section>
  );
};
